/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from "react";
import { logger } from "@/utils/logger";
import { motion, AnimatePresence } from "framer-motion";
import { Home, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";
import { recordGameWon } from "@/utils/playerProfile";

import { getVoiceLang } from "@/utils/voicePref";
const TARGETS = ["🎯", "🪣", "🏀", "⭐", "🎪"];

const speakText = (t) => {
  try { if (!window.speechSynthesis) return; window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(t); u.lang = getVoiceLang(); u.rate=0.85; u.pitch=1.0; window.speechSynthesis.speak(u);
  } catch (err) { logger.debug("game op failed", err); }
};

const playHitSound = () => {
  try { const c = new (window.AudioContext || window.webkitAudioContext)();
    const o = c.createOscillator(); const g = c.createGain();
    o.connect(g); g.connect(c.destination); o.frequency.value=600; o.type="sine";
    g.gain.setValueAtTime(0.3,c.currentTime); g.gain.exponentialRampToValueAtTime(0.001,c.currentTime+0.15);
    o.start(c.currentTime); o.stop(c.currentTime+0.15);
  } catch (err) { logger.debug("game op failed", err); }
};

const LemparBola = ({ onBackToMenu }) => {
  const { width: winW, height: winH } = useWindowSize();
  const [gameState, setGameState] = useState("menu");
  const [playerNames, setPlayerNames] = useState(() => { const s = loadPlayerNames(); return [s[0]||"",s[1]||""]; });
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [scores, setScores] = useState([0, 0]);
  const [round, setRound] = useState(0);
  const [totalRounds] = useState(8);
  const [ballX, setBallX] = useState(50);
  const [ballY, setBallY] = useState(85);
  const [ballVX, setBallVX] = useState(0);
  const [ballVY, setBallVY] = useState(0);
  const [throwing, setThrowing] = useState(false);
  const [targetX, setTargetX] = useState(50);
  const [targetMoving, setTargetMoving] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [power, setPower] = useState(0);
  const [charging, setCharging] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [targetEmoji, setTargetEmoji] = useState("🎯");
  const rafRef = useRef(null);
  const chargeRef = useRef(null);

  const p1 = playerNames[0] || "Pemain 1";
  const p2 = playerNames[1] || "Pemain 2";

  const startGame = () => {
    savePlayerNames(playerNames);
    setScores([0, 0]); setRound(0); setCurrentPlayer(0);
    setGameState("playing");
  };

  const resetBall = useCallback(() => {
    setBallX(50); setBallY(85); setBallVX(0); setBallVY(0);
    setThrowing(false); setFeedback(null); setPower(0); setCharging(false);
    setTargetX(20 + Math.random() * 60);
    setTargetEmoji(TARGETS[Math.floor(Math.random() * TARGETS.length)]);
    setTargetMoving(true);
  }, []);

  useEffect(() => { if (gameState === "playing") resetBall(); }, [gameState, resetBall]);

  // Target movement
  useEffect(() => {
    if (gameState !== "playing" || !targetMoving || throwing) return;
    const id = setInterval(() => {
      setTargetX(prev => {
        let next = prev + (Math.sin(Date.now() / 500) * 0.8);
        return Math.max(15, Math.min(85, next));
      });
    }, 30);
    return () => clearInterval(id);
  }, [gameState, targetMoving, throwing]);

  // Ball physics
  useEffect(() => {
    if (!throwing) return;
    const loop = () => {
      setBallX(prev => prev + ballVX);
      setBallY(prev => {
        const newY = prev + ballVY;
        setBallVY(v => v + 0.15); // gravity
        // Hit target check
        if (newY < 25 && Math.abs(ballX - targetX) < 10) {
          setThrowing(false);
          setFeedback({ type: "hit", text: "KENA!" });
          setScores(prev => { const n = [...prev]; n[currentPlayer] += 1; return n; });
          if (soundOn) { playHitSound(); speakText("Kena!"); }
          setTimeout(() => nextRound(), 1500);
          return newY;
        }
        // Miss
        if (newY < -10 || newY > 100 || ballX < -10 || ballX > 110) {
          setThrowing(false);
          setFeedback({ type: "miss", text: "Meleset!" });
          if (soundOn) speakText("Meleset!");
          setTimeout(() => nextRound(), 1500);
          return newY;
        }
        return newY;
      });
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [throwing, ballVX, ballVY, ballX, targetX, currentPlayer, soundOn]);

  const nextRound = () => {
    if (round + 1 >= totalRounds) {
      setGameState("won");
      recordGameWon("lempar");
      if (soundOn) speakText(scores[0] > scores[1] ? `${p1} wins!` : scores[1] > scores[0] ? `${p2} wins!` : "Draw!");
    } else {
      setRound(prev => prev + 1);
      setCurrentPlayer(prev => prev === 0 ? 1 : 0);
      resetBall();
    }
  };

  const startCharge = () => {
    if (throwing || feedback) return;
    setCharging(true);
    setPower(0);
    chargeRef.current = setInterval(() => {
      setPower(prev => Math.min(prev + 2, 100));
    }, 30);
  };

  const releaseThrow = () => {
    if (!charging) return;
    clearInterval(chargeRef.current);
    setCharging(false);
    const throwPower = power / 100;
    const aimX = (targetX - 50) * 0.04;
    setBallVX(aimX + (Math.random() - 0.5) * 1);
    setBallVY(-4 - throwPower * 6);
    setThrowing(true);
    setTargetMoving(false);
  };

  if (gameState === "menu") {
    return (
      <div className="flex flex-col items-center w-full max-w-2xl mx-auto px-4 pt-4" data-testid="lempar-bola-menu">
        <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass-card-strong rounded-2xl p-6 w-full text-center mb-4">
          <motion.span className="text-6xl block mb-3" animate={{ y: [0,-10,0] }} transition={{ duration: 1, repeat: Infinity }}>🎯</motion.span>
          <h1 className="font-fredoka text-3xl text-gold font-bold mb-2">Lempar Bola</h1>
          <p className="font-fredoka text-gold-light/80 text-sm mb-4">Tahan untuk isi tenaga, lepas untuk lempar!</p>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <input data-testid="lempar-p1" type="text" value={playerNames[0]} onChange={e => setPlayerNames([e.target.value, playerNames[1]])} placeholder="Pemain 1" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
            <input data-testid="lempar-p2" type="text" value={playerNames[1]} onChange={e => setPlayerNames([playerNames[0], e.target.value])} placeholder="Pemain 2" maxLength={15} className="glass-card border-gold/20 rounded-xl px-3 py-2 text-white font-fredoka text-sm focus:outline-none" />
          </div>
          <Button onClick={startGame} className="w-full h-12 font-fredoka font-bold text-lg rounded-xl bg-gradient-to-r from-green-500 to-teal-500" data-testid="lempar-start">MULAI LEMPAR!</Button>
        </motion.div>
        <Button onClick={onBackToMenu} variant="outline" className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl"><Home className="w-4 h-4 mr-2" />Menu</Button>
      </div>
    );
  }

  if (gameState === "won") {
    const winner = scores[0] > scores[1] ? p1 : scores[1] > scores[0] ? p2 : null;
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4" data-testid="lempar-bola-win">
        <Confetti width={winW} height={winH} recycle={false} numberOfPieces={250} />
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="glass-card-strong rounded-2xl p-8 text-center max-w-md w-full">
          <span className="text-6xl block mb-3">🏆</span>
          <h2 className="font-fredoka text-3xl text-gold font-bold mb-2">{winner ? `${winner} Menang!` : "Seri!"}</h2>
          <p className="font-fredoka text-gold-light/80 mb-4">{p1}: {scores[0]} | {p2}: {scores[1]}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => { setGameState("playing"); setScores([0,0]); setRound(0); setCurrentPlayer(0); }} className="w-full font-fredoka font-bold rounded-xl h-10 bg-gradient-to-r from-yellow-400 to-amber-500"><RotateCcw className="w-4 h-4 mr-2" />Main Lagi</Button>
            <Button onClick={() => setGameState("menu")} variant="outline" className="w-full glass-card border-gold/20 text-gold font-fredoka rounded-xl">Menu</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  const containerH = Math.min(winH - 200, 600);
  const containerMaxW = Math.min(winW - 20, 800);

  return (
    <div className="flex flex-col items-center w-full mx-auto px-2" style={{ maxWidth: containerMaxW }} data-testid="lempar-bola-game">
      <div className="w-full glass-card-strong rounded-xl p-2 mb-2 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="font-fredoka text-gold font-bold text-sm">🎯 {round+1}/{totalRounds}</span>
          <span className="font-fredoka text-cyan-400 text-xs">{p1}: {scores[0]}</span>
          <span className="font-fredoka text-pink-400 text-xs">{p2}: {scores[1]}</span>
        </div>
        <div className="flex gap-1.5">
          <Button onClick={() => setSoundOn(!soundOn)} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 w-7 p-0">{soundOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}</Button>
          <Button onClick={() => setGameState("menu")} variant="outline" size="sm" className="glass-card border-gold/20 text-gold h-7 px-2"><Home className="w-3 h-3" /></Button>
        </div>
      </div>

      <div className="font-fredoka text-sm text-center mb-2">
        <span className={currentPlayer === 0 ? "text-cyan-400 font-bold" : "text-pink-400 font-bold"}>Giliran {currentPlayer === 0 ? p1 : p2}</span>
      </div>

      {/* Game area */}
      <div className="w-full rounded-2xl overflow-hidden relative border-2 border-teal-400/30"
        style={{ height: containerH, background: "linear-gradient(180deg, #87CEEB 0%, #B0E0E6 40%, #90EE90 80%, #228B22 100%)" }}>

        {/* Target */}
        <motion.div className="absolute text-5xl z-10 select-none"
          style={{ left: `${targetX}%`, top: '15%', transform: 'translate(-50%, -50%)' }}
          animate={targetMoving ? { scale: [1, 1.1, 1] } : {}}
          transition={{ duration: 1, repeat: Infinity }}>
          {targetEmoji}
        </motion.div>

        {/* Ball */}
        <div className="absolute text-3xl z-20 select-none"
          style={{ left: `${ballX}%`, top: `${ballY}%`, transform: 'translate(-50%, -50%)',
            filter: throwing ? 'drop-shadow(0 0 8px rgba(255,100,0,0.6))' : 'none' }}>
          ⚾
        </div>

        {/* Feedback */}
        <AnimatePresence>
          {feedback && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30"
              data-testid={`lempar-feedback-${feedback.type}`}>
              <span className={`font-fredoka font-bold text-2xl px-6 py-2 rounded-full ${feedback.type === 'hit' ? 'bg-green-500 text-white' : 'bg-red-400 text-white'}`}>
                {feedback.type === 'hit' ? '🎯 KENA!' : '💨 Meleset!'}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Ground decorations */}
        <div className="absolute bottom-0 left-0 right-0 h-[8%] bg-gradient-to-t from-green-800 to-green-600" />
      </div>

      {/* Power bar + Throw button */}
      <div className="w-full mt-3">
        {/* Power indicator */}
        <div className="h-4 rounded-full bg-gray-700 mb-2 overflow-hidden">
          <motion.div className="h-full rounded-full" style={{
            width: `${power}%`,
            background: power < 40 ? '#4ade80' : power < 70 ? '#fbbf24' : '#ef4444',
          }} />
        </div>
        <Button data-testid="lempar-throw"
          onMouseDown={startCharge} onMouseUp={releaseThrow}
          onTouchStart={(e) => { e.preventDefault(); startCharge(); }}
          onTouchEnd={(e) => { e.preventDefault(); releaseThrow(); }}
          disabled={throwing || !!feedback}
          className="w-full h-16 font-fredoka font-bold text-xl rounded-2xl bg-gradient-to-r from-green-400 to-teal-500 btn-3d"
          style={{ touchAction: "manipulation" }}>
          {charging ? `💪 Isi Tenaga: ${power}%` : '🎯 TAHAN untuk LEMPAR!'}
        </Button>
      </div>
    </div>
  );
};

export default LemparBola;
